import type { FooterTemplate, MobileLayoutItem } from '@iheartjane/data-access';
import {
  Card,
  Flex,
  HideEyeIcon,
  InfoIcon,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { SortableItem } from '@jane/shared/components';
import { SortableList } from '@jane/shared/components';

import { MobileLayoutWireframe } from './FooterWireframes';
import { MOBILE_LAYOUT_LABELS } from './utils';

import styles from './MobileLayoutSection.module.css';

const toSortableItems = (
  items: MobileLayoutItem[]
): SortableItem<MobileLayoutItem>[] =>
  items.map((item) => ({ id: item, item }));

const fromSortableItems = (
  items: SortableItem<MobileLayoutItem>[]
): MobileLayoutItem[] =>
  items
    .map((sortableItem) => sortableItem.item)
    .filter((item): item is MobileLayoutItem => item !== undefined);

export const MobileLayoutSection = () => {
  const { control, watch } = useFormContext<FooterTemplate>();
  const mobileLayout = watch('mobile_layout');
  const links = watch('links');
  const callsToAction = watch('calls_to_action');
  const socialLinks = watch('social_links');
  const image = watch('image');
  const textContent = watch('text_content');

  const activeItems = useMemo(() => {
    return (mobileLayout ?? []).filter((item) => {
      if (item === 'links') {
        return links?.some((link) => link.label || link.destination);
      }
      if (item === 'calls_to_action') {
        return !!callsToAction?.[0]?.header;
      }
      if (item === 'social_links') {
        return socialLinks?.links?.some(
          (link) => link.label || link.destination
        );
      }
      if (item === 'image') {
        return !!image;
      }
      if (item === 'text') {
        return !!textContent;
      }
      return false;
    });
  }, [
    JSON.stringify(links),
    JSON.stringify(callsToAction),
    JSON.stringify(socialLinks),
    image,
    textContent,
    mobileLayout,
  ]);

  return (
    <Card
      border="grays-ultralight"
      className={styles['mobileLayoutCard']}
      id="mobile_layout"
    >
      <Flex flexDirection="column" gap={16} p={40}>
        <Typography variant="header-bold">Mobile layout</Typography>
        <Flex alignItems="center" gap={8} mb={24}>
          <Typography>Arrange how these sections display on mobile</Typography>
          <Popover
            target={<InfoIcon size="sm" />}
            label="More info"
            openOn="hover"
            alignment={{ horizontal: 'left', vertical: 'top' }}
          >
            <Popover.Content className={styles['mobileLayoutTooltip']}>
              <Typography textAlign="center" mb={24}>
                Drag these elements to arrange the order they'll appear on a
                mobile device.
              </Typography>
              <MobileLayoutWireframe items={activeItems ?? []} />
            </Popover.Content>
          </Popover>
        </Flex>

        <Controller
          name="mobile_layout"
          control={control}
          render={({ field }) => (
            <SortableList<MobileLayoutItem>
              direction="vertical"
              items={toSortableItems(field.value ?? [])}
              onChange={(newItems) => {
                field.onChange(fromSortableItems(newItems));
              }}
              renderItem={({ item }, index) => {
                if (!item) return null;
                const itemInactive = !activeItems.includes(item);

                return (
                  <Flex
                    data-testid={`mobile-layout-item-${item}-${index}`}
                    alignItems="center"
                    gap={4}
                  >
                    <Typography
                      color={itemInactive ? 'grays-mid' : 'grays-black'}
                    >
                      {MOBILE_LAYOUT_LABELS[item]}{' '}
                    </Typography>

                    {itemInactive && (
                      <Popover
                        alignment={{ horizontal: 'left', vertical: 'top' }}
                        label="Item hidden"
                        openOn="hover"
                        target={
                          <HideEyeIcon ml={8} color="grays-mid" size="sm" />
                        }
                      >
                        <Typography p={24} width="285px">
                          Add content to the {MOBILE_LAYOUT_LABELS[item]}{' '}
                          section to make it visible on mobile.
                        </Typography>
                      </Popover>
                    )}
                  </Flex>
                );
              }}
            />
          )}
        />
      </Flex>
    </Card>
  );
};
